
import { supabase } from '@/lib/supabase';

/**
 * Service for authentication-related API calls
 */
export const authService = {
  /**
   * Sign in with email and password
   */
  signIn: async (email: string, password: string) => {
    console.log("[AuthService] Attempting login for:", email);
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      console.error("[AuthService] Login error:", error);
      throw error;
    }
    
    console.log("[AuthService] Authentication successful for user:", data.user?.id);
    return data.user;
  },
  
  /**
   * Sign out the current user - with enhanced error handling
   * This method will always succeed from the UI perspective even if there are errors
   */
  signOut: async () => {
    console.log("[AuthService] Attempting logout");
    
    // Clear any admin status from localStorage
    try {
      localStorage.removeItem('gildy_admin_user');
    } catch(e) {
      console.error("[AuthService] Error clearing admin localStorage:", e);
    }
    
    // Function to check for active session without triggering errors
    const checkForActiveSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("[AuthService] Error checking session:", error);
          return false;
        }
        return !!data.session;
      } catch (e) {
        console.error("[AuthService] Exception checking session:", e);
        return false;
      }
    };
    
    try {
      // Check if we have an active session before attempting to sign out
      const hasSession = await checkForActiveSession();
      
      if (!hasSession) {
        console.log("[AuthService] No active session, skipping signOut call");
        return;
      }
      
      // Attempt signOut only if we have a session
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.warn("[AuthService] Supabase signOut returned error:", error);
          // Continue - we still want to clear local state
        } else {
          console.log("[AuthService] Supabase signOut successful");
        }
      } catch (signOutError) {
        // Catch and log errors but don't throw them
        console.warn("[AuthService] Exception during signOut:", signOutError);
      }
    } catch (outerError) {
      console.error("[AuthService] Unexpected error in signOut process:", outerError);
    } finally {
      console.log("[AuthService] Logout process completed");
      // We always consider logout a success from the UI perspective
    }
  },
  
  /**
   * Register a new user
   */
  signUp: async (email: string, password: string) => {
    console.log("[AuthService] Attempting registration for:", email);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) {
      console.error("[AuthService] Registration error:", error);
      throw error;
    }
    
    console.log("[AuthService] Registration successful for user:", data.user?.id);
    return data.user;
  },
  
  /**
   * Get current session
   */
  getCurrentSession: async () => {
    console.log("[AuthService] Getting current session");
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error("[AuthService] Get session error:", error);
      throw error;
    }
    
    return data.session;
  },
  
  /**
   * Get current user
   */
  getCurrentUser: async () => {
    console.log("[AuthService] Getting current user");
    const { data, error } = await supabase.auth.getUser();
    
    if (error) {
      console.error("[AuthService] Get user error:", error);
      throw error;
    }
    
    return data.user;
  }
};
